import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ChartComponent } from 'ng-apexcharts';
import { ToastrService } from 'ngx-toastr';
import { Subject, of } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api/api.service';
import { InstitutionalServiceContext } from '../institution-history/traffic-history.models';
import {
  InstitutionContext,
  MonthlyReport,
  MonthlyReportIncident,
  PanelError,
  PanelState,
} from './monthly-report.models';
import {
  MonthlyReportChartOptions,
  buildMonthlyReportDailyChart,
  buildMonthlyReportPdfChart,
  hasDailyTrafficPoints,
} from './utils/monthly-report-chart.util';
import {
  AVAILABILITY_SOURCE_NOTE,
  EMPTY_SERVICES_MESSAGE,
  INCOMPLETE_AVAILABILITY_HINT,
  MANY_SERVICES_MESSAGE,
  MonthOption,
  PDF_CHART_EXPORT_ERROR,
  TRAFFIC_NO_DATA_NOTE,
  TRAFFIC_UNAVAILABLE_NOTE,
  buildMonthOptions,
  canDownloadMonthlyReportPdf,
  ensureMonthOption,
  formatBytesSi,
  formatContractedPlan,
  formatDurationSeconds,
  formatHeaderSubtitle,
  formatIncidentCode,
  formatIncidentPeriod,
  formatLocality,
  formatMbps,
  formatPercent,
  formatUgelContext,
  pdfDownloadTooltip,
  resolveInstitutionalService,
  resolveYearMonthParam,
  ticketCategoryLabel,
  ticketStatusLabel,
  toInstitutionalServicesError,
  toMonthlyReportError,
} from './utils/monthly-report-format.util';
import {
  currentLimaMonth,
  formatLimaMonthLabel,
  shiftYearMonth,
} from '../institution-history/utils/traffic-format.util';
import { monthlyReportPdf } from './pdf/monthly-report-pdf.builder';

export type ServicePanelState = 'loading' | 'empty' | 'many' | 'ready' | 'error';

@Component({
  selector: 'app-institution-monthly-report',
  templateUrl: './institution-monthly-report.component.html',
  styleUrls: ['./institution-monthly-report.component.scss'],
})
export class InstitutionMonthlyReportComponent implements OnInit, OnDestroy {
  clientServiceId: number | null = null;
  resolvedService: InstitutionalServiceContext | null = null;

  serviceState: ServicePanelState = 'loading';
  serviceError: PanelError | null = null;

  yearMonth = '';
  monthOptions: MonthOption[] = [];

  reportState: PanelState = 'loading';
  report: MonthlyReport | null = null;
  header: InstitutionContext | null = null;
  error: PanelError | null = null;
  chart: MonthlyReportChartOptions | null = null;
  pdfChart: MonthlyReportChartOptions | null = null;
  generatingPdf = false;

  @ViewChild('monthlyReportChart') chartRef?: ChartComponent;
  @ViewChild('pdfMonthlyReportChart') pdfChartRef?: ChartComponent;

  readonly skeletonSlots = [1, 2, 3, 4];
  readonly incompleteAvailabilityHint = INCOMPLETE_AVAILABILITY_HINT;
  readonly availabilitySourceNote = AVAILABILITY_SOURCE_NOTE;
  readonly emptyServicesMessage = EMPTY_SERVICES_MESSAGE;
  readonly manyServicesMessage = MANY_SERVICES_MESSAGE;

  private readonly destroy$ = new Subject<void>();
  private readonly requestedMonth$ = new Subject<string>();
  private monthStreamReady = false;

  constructor(
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
    private readonly _api: ApiService,
    private readonly _toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.monthOptions = buildMonthOptions();
    this.loadInstitutionalService();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  retryService(): void {
    this.loadInstitutionalService();
  }

  retry(): void {
    if (!this.yearMonth || this.serviceState !== 'ready') return;
    this.requestedMonth$.next(this.yearMonth);
  }

  onMonthSelect(yearMonth: string): void {
    if (!yearMonth || yearMonth === this.yearMonth) return;
    if (yearMonth > currentLimaMonth()) return;
    this.syncYearMonth(yearMonth, false);
  }

  goPrevMonth(): void {
    if (!this.canGoPrev) return;
    this.syncYearMonth(shiftYearMonth(this.yearMonth, -1), false);
  }

  goNextMonth(): void {
    if (!this.canGoNext) return;
    this.syncYearMonth(shiftYearMonth(this.yearMonth, 1), false);
  }

  get monthLabel(): string {
    return this.yearMonth ? formatLimaMonthLabel(this.yearMonth) : '';
  }

  get canGoNext(): boolean {
    return !!this.yearMonth && this.yearMonth < currentLimaMonth();
  }

  get canGoPrev(): boolean {
    if (!this.yearMonth || this.monthOptions.length === 0) return false;
    const oldest = this.monthOptions[this.monthOptions.length - 1]?.value;
    return !!oldest && this.yearMonth > oldest;
  }

  get pdfTooltip(): string {
    return pdfDownloadTooltip(this.report?.periodStatus, this.report?.downloadAllowed);
  }

  get canDownloadPdf(): boolean {
    return canDownloadMonthlyReportPdf(this.report?.periodStatus, this.report?.downloadAllowed);
  }

  async downloadPdf(): Promise<void> {
    if (!this.canDownloadPdf || this.generatingPdf || !this.report) return;

    this.generatingPdf = true;
    try {
      let chartImage: string | null = null;
      if (hasDailyTrafficPoints(this.report.traffic.dailySeries)) {
        chartImage = await this.exportChartImage();
        if (!chartImage) {
          this._toastr.error(PDF_CHART_EXPORT_ERROR);
          return;
        }
      }

      await monthlyReportPdf.download({
        report: this.report,
        chartImage,
        generatedAt: new Date(),
      });
    } catch {
      this._toastr.error('No se pudo generar el PDF. Inténtalo de nuevo.');
    } finally {
      this.generatingPdf = false;
    }
  }

  private async exportChartImage(): Promise<string | null> {
    const chart = this.pdfChartRef ?? this.chartRef;
    if (!chart?.dataURI) return null;
    try {
      const result = await chart.dataURI({ scale: 3 });
      return result?.imgURI ?? null;
    } catch {
      return null;
    }
  }

  get availabilityPercentLabel(): string | null {
    if (this.report?.availability.status !== 'COMPLETE') return null;
    return formatPercent(this.report.availability.percent);
  }

  get trafficNote(): string | null {
    const traffic = this.report?.traffic;
    if (!traffic) return null;
    if (traffic.identityInconsistent || traffic.legacyNullDaysIgnored) {
      return TRAFFIC_UNAVAILABLE_NOTE;
    }
    if (traffic.dataStatus === 'NO_DATA') {
      return TRAFFIC_NO_DATA_NOTE;
    }
    return null;
  }

  get displayHeader(): InstitutionContext | null {
    return this.header ?? this.resolvedService;
  }

  localityLabel(header: InstitutionContext | null): string {
    return formatLocality(header);
  }

  planLabel(header: InstitutionContext | null): string {
    return formatContractedPlan(header);
  }

  ugelContext(ugel: string | null | undefined): string {
    return formatUgelContext(ugel);
  }

  headerSubtitle(header: InstitutionContext | null): string {
    return formatHeaderSubtitle(header);
  }

  formatMbps(value: number | null | undefined): string {
    return formatMbps(value);
  }

  formatBytes(value: number | null | undefined): string {
    return formatBytesSi(value) ?? '—';
  }

  categoryLabel(value: string | null | undefined): string {
    return ticketCategoryLabel(value);
  }

  statusLabel(value: string | null | undefined): string {
    return ticketStatusLabel(value);
  }

  statusColor(status: string | null | undefined): string {
    const map: Record<string, string> = {
      OPEN: 'open',
      IN_PROGRESS: 'in-progress',
      RESOLVED: 'success',
      CLOSED: 'closed',
      ASSIGNED: 'primary',
      PENDING_CUSTOMER: 'warning',
      CANCELLED: 'cancelled',
    };
    return map[status ?? ''] || 'secondary';
  }

  incidentCode(code: string | null | undefined): string {
    return formatIncidentCode(code);
  }

  incidentPeriod(incident: MonthlyReportIncident): string {
    return formatIncidentPeriod(incident);
  }

  durationLabel(seconds: number): string {
    return formatDurationSeconds(seconds) ?? '—';
  }

  private loadInstitutionalService(): void {
    this.serviceState = 'loading';
    this.serviceError = null;
    this.clientServiceId = null;
    this.resolvedService = null;
    this.header = null;
    this.report = null;
    this.chart = null;
    this.pdfChart = null;
    this.error = null;

    this._api.customer
      .getInstitutionalServices()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (services) => {
          const resolution = resolveInstitutionalService(services);
          if (resolution.kind === 'empty') {
            this.serviceState = 'empty';
            return;
          }
          if (resolution.kind === 'many') {
            this.serviceState = 'many';
            return;
          }

          this.resolvedService = resolution.service;
          this.clientServiceId = resolution.service.clientServiceId;
          this.header = resolution.service;
          this.serviceState = 'ready';
          this.ensureMonthStream();
        },
        error: (err: HttpErrorResponse) => {
          this.serviceError = toInstitutionalServicesError(err);
          this.serviceState = 'error';
        },
      });
  }

  private ensureMonthStream(): void {
    if (this.monthStreamReady) return;
    this.monthStreamReady = true;
    this.setupReportStream();
    this._route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (this.serviceState !== 'ready' || !this.clientServiceId) return;
      const raw = params.get('yearMonth');
      const resolved = resolveYearMonthParam(raw);
      if (raw !== resolved) {
        this.syncYearMonth(resolved, true);
        return;
      }
      this.yearMonth = resolved;
      this.monthOptions = ensureMonthOption(this.monthOptions, resolved);
      this.requestedMonth$.next(resolved);
    });
  }

  private setupReportStream(): void {
    this.requestedMonth$
      .pipe(
        tap(() => this.beginLoad()),
        switchMap((yearMonth) => {
          const id = this.clientServiceId;
          if (!id) {
            return of({ ok: false as const, err: { status: 404 } });
          }
          return this._api.monthlyReport.getMonthlyReport(id, yearMonth).pipe(
            map((data) => ({ ok: true as const, data })),
            catchError((err: HttpErrorResponse) => of({ ok: false as const, err })),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        if (result.ok) {
          this.report = result.data;
          this.header = result.data.header;
          const hasPoints = hasDailyTrafficPoints(result.data.traffic.dailySeries);
          this.chart = hasPoints
            ? buildMonthlyReportDailyChart(result.data.traffic.dailySeries)
            : null;
          this.pdfChart = hasPoints
            ? buildMonthlyReportPdfChart(result.data.traffic.dailySeries)
            : null;
          this.reportState = 'ready';
          this.error = null;
          return;
        }
        this.report = null;
        this.chart = null;
        this.pdfChart = null;
        this.reportState = 'error';
        this.error = toMonthlyReportError(result.err);
      });
  }

  private beginLoad(): void {
    this.reportState = 'loading';
    this.report = null;
    this.chart = null;
    this.pdfChart = null;
    this.error = null;
  }

  private syncYearMonth(yearMonth: string, replaceUrl: boolean): void {
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { yearMonth },
      queryParamsHandling: 'merge',
      replaceUrl,
    });
  }
}
